import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════
// PERSONA MORAL (PM) — Multi-usuario de la cuenta (F7, plan persona-moral).
// Roles, invitación y mappers de la gestión de usuarios del dashboard PM.
// Los shapes de respuesta del BE no están 100% confirmados vs swagger → los
// mappers son defensivos (campos opcionales, tolerantes a ausencias).
// ═══════════════════════════════════════════════════════════════

/** Roles de un usuario dentro de una cuenta PM (ver adapter + 08-security). */
export const accountRoleSchema = z.enum(['administrator', 'authorizer', 'operator']);
export type AccountRole = z.infer<typeof accountRoleSchema>;

/** Estatus de membresía de un usuario en la cuenta. */
export type AccountUserStatus = 'active' | 'pending' | 'removed';

// ───────────────────────────────────────────────────────────────
// Form de invitación (InviteUserModal, F7.3)
// ───────────────────────────────────────────────────────────────

export const inviteUserSchema = z.object({
  email: z.string().trim().email('El correo no es válido'),
  role: accountRoleSchema,
});
export type InviteUserFormData = z.infer<typeof inviteUserSchema>;

// ───────────────────────────────────────────────────────────────
// Tipos de respuesta + mappers (snake_case BE → camelCase FE)
// ───────────────────────────────────────────────────────────────

export interface AccountUser {
  id: string;
  email: string;
  fullName?: string;
  role: AccountRole;
  status: AccountUserStatus;
}

export interface AccountInvitation {
  id: string;
  email: string;
  role: AccountRole;
  status: string;
  expiresAt?: string;
}

const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

/**
 * Mapea un usuario de cuenta del shape del BE. Defensivo: tolera ausencias y
 * cae a defaults seguros (`operator`/`active`) sin lanzar — el BE es la fuente
 * de verdad, este mapeo solo normaliza para la UI.
 */
export function mapAccountUser(raw: Record<string, unknown>): AccountUser {
  const role = asString(raw.role);
  const status = asString(raw.status);
  return {
    id: asString(raw.id) ?? asString(raw.user_id) ?? '',
    email: asString(raw.email) ?? '',
    fullName: asString(raw.full_name) ?? asString(raw.name),
    role: accountRoleSchema.safeParse(role).success ? (role as AccountRole) : 'operator',
    status: status === 'pending' || status === 'removed' ? status : 'active',
  };
}

export function mapAccountInvitation(raw: Record<string, unknown>): AccountInvitation {
  const role = asString(raw.role);
  return {
    id: asString(raw.id) ?? '',
    email: asString(raw.email) ?? '',
    role: accountRoleSchema.safeParse(role).success ? (role as AccountRole) : 'operator',
    status: asString(raw.status) ?? 'pending',
    expiresAt: asString(raw.expires_at),
  };
}
